import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../../redux/store";
import { updateUser } from "../../../../redux/slices/authSlice";
import { Input, Alert, Checkbox } from "@repo/ui";
import { useNavigate } from "react-router-dom";
import { gsap } from "gsap";
import withAuth from "../../../../hoc/withAuth/withAuth";
import AnimatedCard from "../../../../components/AnimatedCard/AnimatedCard";
import StepIndicator from "../../../../components/StepIndicator/StepIndicator";
import { registrationSteps } from "../../constants/steps";
import PageContainer from "../../../../components/PageContainer/PageContainer";
import { updateUserDetails } from "../../../../services/userApiService";
import { withTranslations } from "../../../../hoc/withTranslations/withTranslations";
import NavigationButtons from "../../components/NavigationButtons";
import * as Sentry from "@sentry/react";

const LOCALE = "modules_Registration_pages_AcceptTerms_AcceptTerms";

const TERMS_MATCH_PHRASES = [
  "términos y condiciones",
  "terminos y condiciones",
  "términos y condiciones del sitio",
  "terminos y condiciones del sitio",
  "terms and conditions",
  "terms & conditions",
];

const PRIVACY_MATCH_PHRASES = [
  "política de privacidad",
  "politica de privacidad",
  "aviso de privacidad",
  "privacy policy",
  "privacy policies",
];

const AcceptTerms: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);

  const [name, setName] = useState(user?.name || "");
  const [showNameInput, setShowNameInput] = useState(!user?.name);
  const [acceptTerms, setAcceptTerms] = useState(user?.acceptsTerms || false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(
    user?.acceptsPrivacy || false
  );
  const [alert, setAlert] = useState<{
    message: React.ReactNode;
    level: "info" | "warn" | "error" | "success";
  } | null>(null);

  const alertRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const checkboxesRef = useRef<HTMLDivElement>(null);
  const landingAppBaseUrl = (
    import.meta.env.VITE_LANDING_APP_URL || ""
  ).replace(/\/$/, "");
  const termsUrl = landingAppBaseUrl
    ? `${landingAppBaseUrl}/terms`
    : "/terms";
  const privacyUrl = landingAppBaseUrl
    ? `${landingAppBaseUrl}/privacy`
    : "/privacy";

  const termsLabel = createLabelWithLink(
    t("accept-terms"),
    termsUrl,
    TERMS_MATCH_PHRASES
  );
  const privacyLabel = createLabelWithLink(
    t("accept-privacy"),
    privacyUrl,
    PRIVACY_MATCH_PHRASES
  );

  useEffect(() => {
    if (alertRef.current) {
      gsap.fromTo(
        alertRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 0.5 }
      );
    }
  }, [alert]);

  useEffect(() => {
    if (user?.acceptsTerms && user?.acceptsPrivacy) {
      navigate("/registration/create-gift-table/step1");
    }
  }, [user, navigate]);

  const handleBack = () => {
    navigate("/registration/init");
  };

  const handleNameSubmit = async () => {
    if (name.trim()) {
      try {
        const userId = user?.id || "";
        await updateUserDetails({ id: userId, name });
        dispatch(updateUser({ name }));

        gsap.to(inputRef.current, {
          opacity: 0,
          y: -20,
          duration: 0.5,
          onComplete: () => {
            setShowNameInput(false);
            setTimeout(() => {
              if (checkboxesRef.current) {
                gsap.fromTo(
                  checkboxesRef.current,
                  { opacity: 0, y: 20 },
                  { opacity: 1, y: 0, duration: 0.5 }
                );
              }
            }, 50);
          },
        });
      } catch (error) {
        Sentry.captureException(error);
        setAlert({ message: t("name-update-error"), level: "error" });
      }
    } else {
      setAlert({ message: t("name-error"), level: "warn" });
    }
  };

  const handleContinue = async () => {
    if (!acceptTerms || !acceptPrivacy) {
      setAlert({ message: t("accept-error"), level: "warn" });
      return;
    }

    try {
      const userId = user?.id || "";
      await updateUserDetails({
        id: userId,
        acceptsTerms: acceptTerms,
        acceptsPrivacy: acceptPrivacy,
      });

      dispatch(updateUser({ acceptsTerms: true, acceptsPrivacy: true }));

      navigate("/registration/create-gift-table/step1");
    } catch (error) {
      Sentry.captureException(error);
      setAlert({ message: t("email-error"), level: "error" });
    }
  };

  return (
    <PageContainer>
      <StepIndicator
        steps={registrationSteps}
        currentStep={1}
        progressPercentage={0}
      />
      <AnimatedCard
        title={renderTitle(name, t)}
        className="bg-white max-w-lg w-full"
      >
        {alert && (
          <div ref={alertRef}>
            <Alert message={alert.message} level={alert.level} />
          </div>
        )}
        <div className="flex flex-col space-y-10">
          <p className="text-center my-6">
            {t("introduction")
              .split("\n")
              .map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  <br />
                </React.Fragment>
              ))}
          </p>
          {showNameInput ? (
            <div ref={inputRef}>
              <div className="flex flex-col space-y-6">
                <label htmlFor="name" className="text-label font-paragraph">
                  {t("name-label")}
                </label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  type="text"
                  className="input-border"
                />
                <NavigationButtons
                  continueLabel={t("continue-button")}
                  backLabel={t("backButton", { defaultValue: "Regresar" })}
                  onContinue={handleNameSubmit}
                  onBack={handleBack}
                />
              </div>
            </div>
          ) : (
            <div ref={checkboxesRef} className="space-y-6">
              <Checkbox
                label={termsLabel}
                checked={acceptTerms}
                disabled={user?.acceptsTerms}
                onChange={setAcceptTerms}
              />
              <Checkbox
                label={privacyLabel}
                checked={acceptPrivacy}
                disabled={user?.acceptsPrivacy}
                onChange={setAcceptPrivacy}
              />
              <NavigationButtons
                continueLabel={t("continue-button")}
                backLabel={t("backButton", { defaultValue: "Regresar" })}
                onContinue={handleContinue}
                onBack={handleBack}
              />
            </div>
          )}
        </div>
      </AnimatedCard>
    </PageContainer>
  );
};

const renderTitle = (name: string, t: (key: string) => string) => {
  const trimmedName = name.trim();
  const firstName = trimmedName.split(" ")[0] || "";

  return (
    <span className="flex items-center justify-center">
      {t("title")}
      {trimmedName.length > 0 && (
        <>
          {", "}
          {firstName}!
        </>
      )}
    </span>
  );
};

export default withAuth(withTranslations(LOCALE)(AcceptTerms));

const createLabelWithLink = (
  text: string,
  url: string,
  matchPhrases: string[]
): React.ReactNode => {
  const normalizedText = text.toLowerCase();

  for (const phrase of matchPhrases) {
    const normalizedPhrase = phrase.toLowerCase();
    const matchIndex = normalizedText.indexOf(normalizedPhrase);

    if (matchIndex !== -1) {
      const matchedSegment = text.slice(matchIndex, matchIndex + phrase.length);

      return (
        <>
          {text.slice(0, matchIndex)}
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline"
          >
            {matchedSegment}
          </a>
          {text.slice(matchIndex + phrase.length)}
        </>
      );
    }
  }

  const fallbackText = matchPhrases[0] || text;

  return (
    <>
      {text}
      {text.endsWith(" ") ? "" : " "}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary underline"
      >
        {fallbackText}
      </a>
    </>
  );
};
